import { Mark, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

/**
 * A [[wikilink]] token, emitted by the server's Json render
 * (Objects/Text/Wiki/Render/Json/Ifixit/Wikilink.php) and re-emitted as
 * wikitext by to_wiki.ts. The marked text is the link's display text.
 *
 * A null target is the bare [[Title]] form: the text itself names the
 * linked page, so editing the text retargets the link. A non-null target
 * pins the page ([[Title|display text]]) and the text is free to change.
 */
export const WikiLink = Mark.create({
   name: 'wikiLink',
   inclusive: false,

   addAttributes() {
      return {
         target: {
            default: null,
            parseHTML: (element: HTMLElement) => element.dataset.target ?? null,
            renderHTML: attributes =>
               attributes.target === null ? {} : { 'data-target': attributes.target },
         },
         tag: {
            default: null,
            parseHTML: (element: HTMLElement) => element.dataset.tag ?? null,
            renderHTML: attributes =>
               attributes.tag === null ? {} : { 'data-tag': attributes.tag },
         },
         newWindow: {
            default: false,
            parseHTML: (element: HTMLElement) => element.dataset.newWindow === 'true',
            renderHTML: attributes => (attributes.newWindow ? { 'data-new-window': 'true' } : {}),
         },
         // Where the wiki page lives, for display only — the serialized
         // token carries the title, not the URL.
         href: {
            default: null,
            parseHTML: (element: HTMLElement) => element.getAttribute('href'),
            renderHTML: attributes => (attributes.href === null ? {} : { href: attributes.href }),
         },
      };
   },

   parseHTML() {
      return [{ tag: 'a[data-wikilink]' }];
   },

   renderHTML({ HTMLAttributes }) {
      return ['a', mergeAttributes({ 'data-wikilink': '' }, HTMLAttributes), 0];
   },

   // The mark renders a real <a href> so the wikilink looks like a link and
   // keeps its destination for a future "open link" affordance. But inside
   // the editor that href is a trap: clicking it navigates away from the page
   // (the browser's loading bar, then the wiki page) instead of placing the
   // caret to edit. Swallow the anchor's default navigation so a click just
   // moves the cursor into the link, like any other editable text.
   addProseMirrorPlugins() {
      return [
         new Plugin({
            key: new PluginKey('wikiLinkClick'),
            props: {
               handleDOMEvents: {
                  click: (_view, event) => {
                     const target = event.target;
                     if (target instanceof Element && target.closest('a[data-wikilink]')) {
                        event.preventDefault();
                        // The global page loader (Shared/utils/loader.ts) has a
                        // document-level click handler that starts its progress
                        // bar for internal anchor clicks and doesn't check
                        // defaultPrevented. Without stopping propagation, an
                        // in-editor wikilink click leaves a loading bar with no
                        // navigation to ever clear it.
                        event.stopPropagation();
                     }
                     return false;
                  },
               },
            },
         }),
      ];
   },
});
